'use client';

import { FC, useState, useLayoutEffect, useMemo } from 'react';
import { Link } from '@/react/components/core-ui/Link';
import { useLocaleMessage } from '@/react/hooks/useLocaleMessage';
import { Icon, IconTypes, IconSizeProp } from '@/react/components/core-ui/Icon';
import { FreeShippingInformationModal } from '@/react/components/product-details/FreeShippingIconBlock/FreeShippingInformationModal';
import { useModal } from '@/react/hooks/useModal';
import {
  Breakpoint,
  Breakpoints
} from '@/react/components/core-ui/Breakpoints';
import { Button } from '@/react/components/core-ui/Button';
import type { Content } from '@/services/isomorphic/ContentService';
import { isNullish } from '@/utils/null-utils';
import { DirectContent } from '@/react/components/content/core/DirectContent';
import type { Nullable } from '@/type-utils';
import { TopCountrySelector } from '@/react/components/country/TopCountrySelector/';
import { useScroll } from '@/react/components/core-ui/hooks/useScroll';
import { classes } from '@/next-utils/css-utils/scss-utils';
import { GlobalCSSCustomProperty } from '@/type-utils/css';
import { useGlobalCSSCustomProperty } from '@/react/components/core-ui/hooks/useGlobalCSSCustomProperty';
import { createOnResizeRef } from '@/react/utils/ref-utils/createOnResizeRef';
import { DataModelTarget } from '@/services/isomorphic/integrations/BuilderService';
import UserButton from '../UserButton';

import S from './styles.base.module.scss';
import { header_utilBar_guaranteeLink } from "@/lang/__generated__/ahnu/header_utilBar_guaranteeLink";
import { header_utilBar_storeLocator } from "@/lang/__generated__/ahnu/header_utilBar_storeLocator";

/**
 * Default debounce time for the useScroll hook.
 */
const DEFAULT_DEBOUNCE_TIME = 100;

export interface ITopUtilityBarProps {
  /**
   * This is the banner to be displayed in the utility bar.
   */
  contentBanner?: Nullable<Content>;

  /**
   * Show the user button in the main header or the top utility bar.
   */
  userButtonInHeader: boolean;
}

/**
 * Checks if the content banner is a slider.
 * @param contentBanner - The content banner to check.
 * @returns True if the content banner is a slider, false otherwise.
 */
const getIsBannerSlider = (contentBanner: Nullable<Content>): boolean => {
  if (isNullish(contentBanner)) return false;
  if (contentBanner.type !== DataModelTarget.MarketingBannerModel) return false;

  return contentBanner?.dataModel?.isSlider ?? false;
};

/**
 * Top utility bar above the nav bar with a promotional link and My Account / Store Locator links.
 */
export const TopUtilityBar: FC<ITopUtilityBarProps> = ({
  contentBanner = null,
  userButtonInHeader
}) => {
  const [msg] = useLocaleMessage();
  const [hidden, setHidden] = useState(false);
  const { scrollY } = useScroll(DEFAULT_DEBOUNCE_TIME);
  const [headerUtilityBarHeight, setHeaderUtilityBarHeight] = useState(0);

  /**
   * On resize, update the header utility bar height.
   *
   * The `onResizeRef` is necessary to ensure the bar height will be updated correctly, since the content is
   * dynamically added from CMS, it may contain sliders or not, and other unknown variables.
   *
   * @see {@link createOnResizeRef} for more details.
   */
  const updateHeaderUtilityBarHeightRef = useMemo(
    () =>
      createOnResizeRef<HTMLDivElement>(
        (elt) => {
          setHeaderUtilityBarHeight(elt.getBoundingClientRect().height);
        },
        () => setHeaderUtilityBarHeight(0)
      ),
    []
  );

  useGlobalCSSCustomProperty(
    GlobalCSSCustomProperty.HeaderUtilityBarHeight,
    `${headerUtilityBarHeight}px`,
    [headerUtilityBarHeight]
  );

  useGlobalCSSCustomProperty(
    GlobalCSSCustomProperty.HeaderUtilityBarTop,
    hidden ? `-${headerUtilityBarHeight}px` : '0px',
    [hidden, headerUtilityBarHeight]
  );

  useLayoutEffect(() => {
    setHidden(scrollY > headerUtilityBarHeight);
  }, [scrollY, headerUtilityBarHeight]);
  // Leverage the useModal() hook to show / hide the modal.
  const {
    isOpen: isModalOpen,
    open: handleModalOpen,
    close: handleModalClose
  } = useModal();

  const isBannerSlider = getIsBannerSlider(contentBanner);

  return (
    <div
      className={classes(S.utilityWrapper, {
        [S.hidden]: hidden,
        [S.isTall]: isBannerSlider
      })}
      ref={updateHeaderUtilityBarHeightRef}
    >
      <Breakpoints>
        <Breakpoint media="desktop">
          {!isNullish(contentBanner) &&
          'hasContent' in contentBanner &&
          contentBanner.hasContent ? (
            <div className={S.promoLink}>
              <DirectContent content={contentBanner} />
            </div>
          ) : (
            <div className={S.promoLink}>
              <FreeShippingInformationModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
              />
              <Button
                className={S.link}
                onClick={handleModalOpen}
                variant="text"
              >
                {msg(header_utilBar_guaranteeLink)}
                <Icon
                  className={S.icon}
                  size={IconSizeProp.SizeSM}
                  icon={IconTypes.QuestionCircle}
                />
              </Button>
            </div>
          )}
          <div className={S.utilityLinks}>
            <div className={S.storeLocator}>
              <Link className={S.link} href="/storelocator" variant="text">
                <Icon
                  className={S.icon}
                  icon={IconTypes.MapMarker}
                  size={IconSizeProp.Size1X}
                />
                {msg(header_utilBar_storeLocator)}
              </Link>
            </div>
            {!userButtonInHeader && (
              <div className={S.account}>
                <UserButton />
              </div>
            )}
            <TopCountrySelector />
          </div>
        </Breakpoint>
        <Breakpoint default>
          {!isNullish(contentBanner) &&
          'hasContent' in contentBanner &&
          contentBanner.hasContent ? (
            <div className={S.promoLink}>
              <DirectContent content={contentBanner} />
            </div>
          ) : (
            <>
              {/* Mobile / Tablet promotion link. */}
              <div className={S.promoLink}>
                <FreeShippingInformationModal
                  isOpen={isModalOpen}
                  onClose={handleModalClose}
                />
                <Button
                  className={S.link}
                  onClick={handleModalOpen}
                  variant="text"
                >
                  {msg(header_utilBar_guaranteeLink)}
                  <Icon
                    className={S.icon}
                    size={IconSizeProp.SizeSM}
                    icon={IconTypes.QuestionCircle}
                  />
                </Button>
              </div>
            </>
          )}
        </Breakpoint>
      </Breakpoints>
    </div>
  );
};
